// React and related imports
import React, { useMemo } from 'react';

// Flowbite components
import { Table } from 'flowbite-react';

// i18 Translation
import { useTranslation } from 'react-i18next';

// Custom Components
import { TableCell } from '../Table/Table';
import { EditButton } from '../Shared/EditButton';
import { StatusBadge } from '../Shared/StatusBadge/StatusBadge';

// Types and Interfaces
import { ISessionWithStats } from '../../common/interfaces/Session.interface';

// Redux
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';

// Utils
import { getLocalizedValueFromMultiLangValues } from '../../utils/getLocalizedValueFromMultiLangValues';
import { truncateText } from '../../utils/truncateText';
import { shortenLink } from '../../utils/shortenLink';
import { getTitleTooltip } from '../../utils/getTitleTooltip';
import { getSessionStatusVariant } from './utils';

interface SessionTableBodyProps {
  sessions: ISessionWithStats[];
  onEdit: (session: ISessionWithStats) => void;
}

export const SessionTableBody: React.FC<SessionTableBodyProps> = ({
  sessions,
  onEdit,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = useAppSelector(selectIsRTL);
  const currentLanguage = i18n.language;
  const noDataPlaceholder = '--';

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(currentLanguage, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const rows = useMemo(() => {
    return sessions.map((session) => {
      const title = getLocalizedValueFromMultiLangValues(session.title);
      const description = getLocalizedValueFromMultiLangValues(
        session.description
      );
      const truncatedTitle =
        truncateText(title || '', 30, currentLanguage, isRtl) ||
        noDataPlaceholder;
      const truncatedDescription =
        truncateText(description || '', 40, currentLanguage, isRtl) ||
        noDataPlaceholder;
      const shortenedUrl = session.url
        ? shortenLink(session.url, 30, isRtl)
        : noDataPlaceholder;

      return {
        id: session.id,
        title,
        description,
        truncatedTitle,
        truncatedDescription,
        shortenedUrl,
        url: session.url,
        status: session.status,
        totalAssignments: session.totalAssigned || 0,
        completedCount: session.totalCompleted || 0,
        createdAt: session.createdAt,
        session: session as ISessionWithStats,
      };
    });
  }, [sessions, currentLanguage, isRtl]);

  return (
    <Table.Body>
      {rows.map(
        ({
          id,
          title,
          description,
          truncatedTitle,
          truncatedDescription,
          shortenedUrl,
          url,
          status,
          totalAssignments,
          completedCount,
          createdAt,
          session,
        }) => (
          <Table.Row key={id} className="bg-white">
            <TableCell>{getTitleTooltip(title, truncatedTitle)}</TableCell>
            <TableCell>
              {getTitleTooltip(description, truncatedDescription)}
            </TableCell>
            <TableCell>
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200"
                >
                  {shortenedUrl}
                </a>
              ) : (
                noDataPlaceholder
              )}
            </TableCell>
            <TableCell>
              <StatusBadge variant={getSessionStatusVariant(status)}>
                {t(`status.${status}`)}
              </StatusBadge>
            </TableCell>
            <TableCell>{totalAssignments}</TableCell>
            <TableCell>{completedCount}</TableCell>
            <TableCell>{formatDate(createdAt)}</TableCell>
            <TableCell>
              <EditButton onClick={() => onEdit(session)} />
            </TableCell>
          </Table.Row>
        )
      )}
    </Table.Body>
  );
};
